import React, { Fragment, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import AuthContext from '../../../auth/AuthContext';
import UserService from '../../../services/UserService';
import AlertService from '../../../services/AlertService';
import MfaReconfigureIcon from '../../../assets/icons/TwoFactorIcon.png';
import { Skeleton } from 'primereact';

const MfaReconfigure = () => {
  const navigate = useNavigate();
  const params = useParams();
  const authContext = useContext(AuthContext);

  const authRef = useRef(authContext);

  const [loading, setLoading] = useState(true);
  const [qrCode, setQrCode] = useState('');
  const [qrCodeLoaded, setQrCodeLoaded] = useState(false);
  const [otp, setOtp] = useState(Array(6).fill(''));
  const [codeIsValid, setCodeIsValid] = useState(true);
  const calledRef = useRef(false);

  const loadQrCode = useCallback(async () => {
    if (calledRef.current) return;
    calledRef.current = true;

    if (!params.code) {
      AlertService.error("Invalid or missing QR configuration code in URL.");
      navigate("/profile");
      return;
    }

    setLoading(true);
    try {
      const data = await UserService.ReConfigureQrCode(params.code, authRef.current);
      if (data.isSuccess) {
        setQrCode(data.data.qrCodeSetupImageUrl);
        setQrCodeLoaded(true);
      } else {
        AlertService.error(data.errorMessage || "Failed to load QR Code.");
      }
    } catch (err) {
      AlertService.error("Unexpected error while loading QR Code.");
    }
    setLoading(false);
  }, [params.code, navigate]);

  useEffect(() => {
    loadQrCode();
  }, [loadQrCode]);

  const handleOtpChange = (e, index) => {
    const val = e.target.value.replace(/[^0-9]/g, '');
    if (val.length <= 1) {
      setCodeIsValid(true);
      const newOtp = [...otp];
      newOtp[index] = val;
      setOtp(newOtp);

      if (val && index < otp.length - 1) {
        const nextInput = document.getElementById(`otp-${index + 1}`);
        if (nextInput) nextInput.focus();
      }
    }
  };

  const handleOtpKeyDown = (e, index) => {
    if (e.key === 'Backspace') {
      if (otp[index] === '' && index > 0) {
        const prevInput = document.getElementById(`otp-${index - 1}`);
        if (prevInput) prevInput.focus();
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`);
      if (prevInput) prevInput.focus();
    } else if (e.key === 'ArrowRight' && index < otp.length - 1) {
      const nextInput = document.getElementById(`otp-${index + 1}`);
      if (nextInput) nextInput.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasteData = (e.clipboardData || window.clipboardData).getData('text');
    const digits = pasteData.replace(/[^0-9]/g, '').slice(0, otp.length).split('');
    if (digits.length === 0) return;
    const newOtp = Array(otp.length).fill('');
    digits.forEach((d, i) => (newOtp[i] = d));
    setOtp(newOtp);
    const focusIndex = digits.length < otp.length ? digits.length : otp.length - 1;
    setTimeout(() => {
      const field = document.getElementById(`otp-${focusIndex}`);
      if (field) field.focus();
    }, 10);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const finalOtp = otp.join('');
    if (finalOtp.length !== otp.length) {
      setCodeIsValid(false);
      AlertService.error(`Please enter a valid ${otp.length}-digit code`);
      return;
    }
    setLoading(true);
    try {
      const result = await UserService.VerifyQrCode(finalOtp, authRef.current);
      if (result.isSuccess) {
        authRef.current.setMFASetupCompleted(true);
        AlertService.success("Two Factor Authentication setup completed successfully.");
        navigate("/logout");
      } else {
        AlertService.error(result.errorMessage || "Verification failed.");
      }
    } catch (error) {
      AlertService.error("Something went wrong during verification.");
      console.error(error);
    }
    setLoading(false);
  };

  return (
    <Fragment>
      <div className="d-flex profile container-fluid pt-3 mt-4">
        <div className="OtpVerify-left d-none d-xl-block">
          <center>
            <img src={MfaReconfigureIcon} alt='Two Factor Icon' className='img-fluid' />
          </center>
        </div>
        <div className="profile-right d-flex flex-column justify-content-center mx-auto pt-5 mt-5  pt-xl-0 mt-xl-0" style={{ maxWidth: '550px' }}>
          {loading && !qrCodeLoaded ? (
            <>
              <center><Skeleton width="60%" height="3rem" className="mb-3" /></center>

              <div className="d-flex justify-content-center mb-3 mt-3">
                <Skeleton width="80%" height="3rem" />
              </div>

              <div className="d-flex justify-content-center mb-4 mt-2">
                <Skeleton size="10rem" shape="circle" />
              </div>

              <div className="d-flex justify-content-center mb-3">
                <Skeleton width="70%" height="3rem" />
              </div>

              <div className="d-flex justify-content-between mb-4">
                {[...Array(6)].map((_, i) => (
                  <Skeleton key={i} width="3rem" height="3rem" className="rounded" />
                ))}
              </div>

              <Skeleton width="100%" height="3rem" />
            </>
          ) : qrCodeLoaded ? (
            <>
              <center><h4>Two Factor Verification Setup</h4></center>
              <p className='text-center mt-3'>Please scan the QR code displayed below using Microsoft Authenticator.</p>
              <div className="d-flex justify-content-center mb-4 mt-2">
                <img src={qrCode} alt="QR Setup" style={{ maxWidth: '10rem', width: '100%' }} />
              </div>
              <p className="mb-3 text-center">
                Once your email is added to the list, verify the One-Time Password code generated by your authenticator app in the textbox below.
              </p>
              <Form onSubmit={handleSubmit}>
                <div className="d-flex justify-content-between mb-4" onPaste={handlePaste}>
                  {otp.map((digit, i) => (
                    <input
                      key={i}
                      id={`otp-${i}`}
                      type="text"
                      maxLength="1"
                      className="form-control text-center mfa-input gap-2 custom-input-color"
                      value={digit}
                      onChange={(e) => handleOtpChange(e, i)}
                      onKeyDown={(e) => handleOtpKeyDown(e, i)}
                      autoComplete="off"
                      autoFocus={i === 0} 
                      disabled={loading}
                      aria-label={`OTP digit ${i + 1}`}
                    />
                  ))}
                </div>
                {!codeIsValid && (
                  <div className="text-danger mb-2">
                    Please enter a valid {otp.length}-digit code
                  </div>
                )}
                <Button
                  type="submit"
                  className="btn w-100 btn-primary custom-input mt-2"
                  disabled={loading}
                >
                  {loading ? (
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                  ) : null}
                  {loading ? 'Processing...' : 'Verify & Complete Setup'}
                </Button>
              </Form>
            </>
          ) : (
            <>
              <center><Skeleton width="60%" height="3rem" className="mb-3" /></center>

              <div className="d-flex justify-content-center mb-3 mt-3">
                <Skeleton width="80%" height="3rem" />
              </div>

              <div className="d-flex justify-content-center mb-4 mt-2">
                <Skeleton size="10rem" shape="circle" />
              </div>

              <div className="d-flex justify-content-center mb-3">
                <Skeleton width="70%" height="3rem" />
              </div>

              <div className="d-flex justify-content-between mb-4">
                {[...Array(6)].map((_, i) => (
                  <Skeleton key={i} width="3rem" height="3rem" className="rounded" />
                ))}
              </div>

              <Skeleton width="100%" height="3rem" />
            </>
          )}
        </div>
      </div>
    </Fragment>
  );
};

export default MfaReconfigure;
